package com.banyan.flightviewer.web.dom

import kotlinx.browser.document
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import org.w3c.dom.Element
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLSpanElement
import org.w3c.dom.HTMLTableCellElement
import org.w3c.dom.HTMLTableElement
import org.w3c.dom.HTMLTableRowElement
import org.w3c.dom.HTMLTableSectionElement
import org.w3c.dom.events.Event

/**
 * Small DOM-builder DSL. Each helper creates an element, optionally sets the
 * className, runs a builder lambda, appends to the receiver, and returns the
 * element so callers can keep references for later mutation.
 *
 * Used in place of kotlin-react / kotlin-wrappers because the latter has a
 * tight Kotlin-version coupling that conflicts with our Compose-pinned stack.
 */
inline fun Element.div(className: String? = null, build: HTMLDivElement.() -> Unit = {}): HTMLDivElement {
    val el = document.createElement("div") as HTMLDivElement
    if (className != null) el.className = className
    el.build()
    appendChild(el)
    return el
}

inline fun Element.span(className: String? = null, build: HTMLSpanElement.() -> Unit = {}): HTMLSpanElement {
    val el = document.createElement("span") as HTMLSpanElement
    if (className != null) el.className = className
    el.build()
    appendChild(el)
    return el
}

inline fun Element.button(className: String? = null, build: HTMLButtonElement.() -> Unit = {}): HTMLButtonElement {
    val el = document.createElement("button") as HTMLButtonElement
    if (className != null) el.className = className
    el.build()
    appendChild(el)
    return el
}

inline fun Element.a(className: String? = null, build: HTMLAnchorElement.() -> Unit = {}): HTMLAnchorElement {
    val el = document.createElement("a") as HTMLAnchorElement
    if (className != null) el.className = className
    el.build()
    appendChild(el)
    return el
}

inline fun Element.table(className: String? = null, build: HTMLTableElement.() -> Unit = {}): HTMLTableElement {
    val el = document.createElement("table") as HTMLTableElement
    if (className != null) el.className = className
    el.build()
    appendChild(el)
    return el
}

inline fun Element.thead(build: HTMLTableSectionElement.() -> Unit = {}): HTMLTableSectionElement {
    val el = document.createElement("thead") as HTMLTableSectionElement
    el.build()
    appendChild(el)
    return el
}

inline fun Element.tbody(build: HTMLTableSectionElement.() -> Unit = {}): HTMLTableSectionElement {
    val el = document.createElement("tbody") as HTMLTableSectionElement
    el.build()
    appendChild(el)
    return el
}

inline fun Element.tr(className: String? = null, build: HTMLTableRowElement.() -> Unit = {}): HTMLTableRowElement {
    val el = document.createElement("tr") as HTMLTableRowElement
    if (className != null) el.className = className
    el.build()
    appendChild(el)
    return el
}

inline fun Element.th(className: String? = null, text: String? = null, build: HTMLTableCellElement.() -> Unit = {}): HTMLTableCellElement {
    val el = document.createElement("th") as HTMLTableCellElement
    if (className != null) el.className = className
    if (text != null) el.textContent = text
    el.build()
    appendChild(el)
    return el
}

inline fun Element.td(className: String? = null, text: String? = null, build: HTMLTableCellElement.() -> Unit = {}): HTMLTableCellElement {
    val el = document.createElement("td") as HTMLTableCellElement
    if (className != null) el.className = className
    if (text != null) el.textContent = text
    el.build()
    appendChild(el)
    return el
}

fun Element.text(value: String) {
    appendChild(document.createTextNode(value))
}

fun Element.removeAllChildren() {
    while (firstChild != null) {
        removeChild(firstChild!!)
    }
}

/** Listen for click events; useful for typed onClick handlers. */
fun Element.onClick(handler: (Event) -> Unit) {
    addEventListener("click", { event -> handler(event) })
}

/**
 * Collect [flow] in [scope], invoking [onValue] for the initial value and
 * each subsequent emission. Use to drive imperative DOM updates from shared
 * StateFlows.
 */
fun <T> CoroutineScope.bindFlow(flow: StateFlow<T>, onValue: (T) -> Unit) {
    onValue(flow.value)
    launch {
        flow.collect { onValue(it) }
    }
}
